import {Component, inject, Input, OnChanges, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {ReactiveFormsModule} from "@angular/forms";
import {MatButtonModule} from "@angular/material/button";
import {MatOptionModule} from "@angular/material/core";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {MatSelectModule} from "@angular/material/select";
import {MatTooltipModule} from "@angular/material/tooltip";
import {environment} from "@env/environment";
import {BillingUpload} from "@features/leasing/billing-details-upload/billing-details-upload.model";
import {LeaseUploadService} from "@features/leasing/lease-upload/lease-upload.service";
import {LoadingService} from "@ui-kit/utils/service/loading.service";
import {CrudTableComponent} from "@ui-webkit/components/crud-table/crud-table.component";
import {CsvImportComponent} from "@ui-webkit/components/csv-import/csv-import.component";
import {UploadDataModel, UploadResponse} from "@ui-webkit/components/csv-import/csv-import.model";
import {CreateBatchLease, Lease, LeaseSummary, LeaseUpload, leaseUploadModel} from "./lease-upload.model";

@Component({
  selector: 'app-lease-upload',
  standalone: true,
  imports: [
    CrudTableComponent,
    CsvImportComponent,
    MatFormFieldModule,
    MatOptionModule,
    MatSelectModule,
    MatButtonModule,
    MatTooltipModule,
    ReactiveFormsModule,
    MatInputModule
  ],
  templateUrl: './lease-upload.component.html',
  styleUrl: './lease-upload.component.scss'
})
export class LeaseUploadComponent implements OnInit, OnChanges {
  @ViewChild('csvImport') csvImport!: CsvImportComponent;
  @Input() parentLease!: Lease;

  private _loader = inject(LoadingService);
  protected loading$ = this._loader.loading$;

  uploadConfig: UploadDataModel[] = leaseUploadModel;
  disableUpload: boolean = true;
  uploadEndpoint = '';
  validateEndpoint = '';

  subLeases: Lease[] = [];

  protected readonly leaseSummaryModel = LeaseSummary;

  constructor(private leaseUploadService: LeaseUploadService) {
    this.uploadConfig.find(f => f.property == 'leaseParentUuid')!.computeMethod = this.setParentUuid.bind(this);
  }

  ngOnInit() {
    this.loadSubLeases();
    this.setEndpoints();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['parentLease']) {
      this.loadSubLeases();
      this.setEndpoints();
    }
  }

  setEndpoints() {
    this.uploadEndpoint = this.baseUrl + '/create';
    this.validateEndpoint = this.baseUrl + '/validate';
  }

  loadSubLeases() {
    this.leaseUploadService.getSubLeases(this.parentLease.uuid).subscribe({
      next: data => {
        this.subLeases = data.content;
      },
      error: err => {
        console.log(err);
      }
    })
  }

  setParentUuid(data: any) {
    return this.parentLease!.uuid;
  }

  updateDataAfterValidation(data: {req: LeaseUpload[], res: CreateBatchLease}) {
    const dataToUpload = [...data.req];
    dataToUpload.forEach(line => {
      const resForLine = data.res.subLeases.find(f => f.retailerCompanyName == line.retailerCompanyName);
      line.errorMessages = [];
      if (data.res.errorMessage != null) line.errorMessages.push(data.res.errorMessage);
      if (resForLine) {
        line.userId = resForLine.userId;
        line.retailerId = resForLine.retailerId;
        if (resForLine.errorMessages && resForLine.errorMessages.length > 0) {
          line.errorMessages.push(...resForLine.errorMessages);
        }
      }
    })

    const allErrors = dataToUpload.flatMap(m => m.errorMessages)
      .filter(f => !!f);
    if (data.res.errorMessage) allErrors.push(data.res.errorMessage);
    this.disableUpload = allErrors.length > 0
    this.csvImport.overwriteData(dataToUpload);
  }

  updateAfterUpload(data: {req: BillingUpload[], res: UploadResponse}) {
    if (data.res.errorsMessages != null && data.res.errorsMessages.length > 0) {
      this.csvImport.fileSuccessfullyUploaded(data.req, data.res);
    }
    this.loadSubLeases();
  }

  get baseUrl() {
    if (this.parentLease) return environment.apiLeaseHost + `/console/manage-lease/${this.parentLease.uuid}/sub-lease/batch`;
    return '';
  }
}
